import { Injectable, NgZone, inject } from '@angular/core';
import { User as Auth0User } from '@auth0/auth0-spa-js';
import { AppState, AuthService } from '@auth0/auth0-angular';
import {
  EMPTY,
  forkJoin,
  from,
  Observable,
  of,
  race,
  Subscription,
  timer,
} from 'rxjs';
import {
  catchError,
  filter,
  map,
  mergeMap,
  switchMap,
  take,
  tap,
} from 'rxjs/operators';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { Browser } from '@capacitor/browser';
import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';
import { callbackUri } from 'src/app/clients/mobile/mobile-app.module';
import { Router } from '@angular/router';
import { MobileRoutesHelper } from '../../core/helpers/mobile-routes.helper';
import { environment } from 'src/environments/environment';
import { AuthUser } from 'src/app/auth/models/auth-user.model';
import { LogIn, LogOut } from 'src/app/auth/store/auth.actions';
import { PreferencesService } from '../../biometrics/services/preferences.service';
import {
  BiometricsActions,
  BiometricSecurityType,
} from '../../biometrics/store/biometrics.store';
import { MobileAuthActions } from '../store/mobile-auth.actions';
import { LocationService } from '../../../../core/services/location.service';

// NOTE: iOS SFSafariViewController hard URL limit (empirical ~2048 chars).
const IOS_URL_LIMIT = 2048;
// NOTE: Backoff schedule (ms) for Browser.open() retries — first attempt is immediate.
const BROWSER_OPEN_BACKOFFS_MS = [0, 600, 1800];

@Injectable()
export class MobileAuthService {
  private readonly store = inject<Store<AppState>>(Store);
  private readonly router = inject(Router);
  private readonly zone = inject(NgZone);
  private readonly auth0Service = inject(AuthService);
  private readonly preferencesService = inject(PreferencesService);
  private readonly locationService = inject(LocationService);
  private readonly actions$ = inject(Actions);

  private isProcessingCallback = false;
  private isLoginFlowInProgress = false;
  private isLoggingOut = false;
  private logoutTimeoutSub: Subscription | null = null;

  constructor() {
    App.addListener('appUrlOpen', ({ url }) => {
      console.log('[MobileAuthService] appUrlOpen received:', url);

      this.zone.run(() => {
        // Validar que la URL sea nuestro callback
        if (!url?.startsWith(callbackUri)) {
          console.log(
            '[MobileAuthService] URL does not match callbackUri, ignoring'
          );
          return;
        }

        // Prevenir procesamiento duplicado
        if (this.isProcessingCallback) {
          console.warn(
            '[MobileAuthService] Already processing callback, ignoring duplicate'
          );
          return;
        }

        // Detectar tipo de callback: login tiene state + code/error, logout no
        const isLoginCallback =
          url.includes('state=') &&
          (url.includes('code=') || url.includes('error='));

        if (isLoginCallback) {
          console.log('[MobileAuthService] Detected LOGIN callback');
          // NOTE: Positive signal so MobileAuthorizeComponent cancels its
          // delayed / recovery timers. The post-login pipeline
          // (handleRedirectCallback → forkJoin → biometrics → LogIn) can take
          // longer than the 20s recovery timer, and without this the user
          // sees the retry UI flash before LogIn finally dispatches.
          this.store.dispatch(MobileAuthActions.loginCallbackReceived());
          this.handleLoginCallback(url);
        } else {
          console.log('[MobileAuthService] Detected LOGOUT callback');
          this.handleLogoutCallbackWithoutState(url);
        }
      });
    });
  }

  // ---------------------------------------------------------------------------
  // LOGIN CALLBACK
  // ---------------------------------------------------------------------------
  private handleLoginCallback(url: string): void {
    this.isProcessingCallback = true;

    this.auth0Service
      .handleRedirectCallback(url)
      .pipe(
        mergeMap(() => {
          console.log(
            '[MobileAuthService] handleRedirectCallback completed, starting forkJoin'
          );

          return forkJoin([
            this.getAuth0User().pipe(take(1)),
            this.auth0Service.isAuthenticated$.pipe(take(1)),
            this.getTokenSilently().pipe(
              take(1),
              catchError(tokenError => {
                console.error(
                  '[MobileAuthService] Error getting token:',
                  tokenError
                );
                return of(null);
              })
            ),
          ]).pipe(
            mergeMap(([user, isAuthenticated, token]) => {
              console.log('[MobileAuthService] forkJoin completed:', {
                hasUser: !!user,
                isAuthenticated,
                hasToken: !!token,
              });

              // NOTE: Do not dispatch LogIn with null token — store would have token: null,
              // AuthGuard would redirect to auth/authorize, causing an infinite loop.
              if (!isAuthenticated || !token) {
                console.warn(
                  '[MobileAuthService] Authentication incomplete after callback (isAuthenticated=%s, hasToken=%s), restarting login',
                  isAuthenticated,
                  !!token
                );
                this.resetFlags();
                return this.loginWithRedirect({ cleanState: true });
              }

              this.store.dispatch(
                new LogIn({
                  token,
                  user: AuthUser.fromAuth0(user),
                })
              );

              // Wait for biometrics setup flow to complete before redirecting
              // This prevents race condition with securityComplete$ effect
              return this.waitForBiometricsCompletion(url).pipe(
                mergeMap(() => {
                  console.log('[MobileAuthService] Closing browser');
                  return from(Browser.close()).pipe(
                    catchError(closeError => {
                      console.error(
                        '[MobileAuthService] Error closing browser:',
                        closeError
                      );
                      return of(void 0);
                    })
                  );
                }),
                switchMap(() => {
                  this.resetFlags();
                  console.log(
                    '[MobileAuthService] Browser closed, flags reset after login'
                  );
                  return of(void 0);
                })
              );
            }),
            catchError(forkJoinError => {
              console.error(
                '[MobileAuthService] Error in forkJoin:',
                forkJoinError
              );
              this.resetFlags();
              return from(Browser.close()).pipe(
                catchError(closeError => {
                  console.error(
                    '[MobileAuthService] Error closing browser after forkJoin error:',
                    closeError
                  );
                  return of(void 0);
                }),
                switchMap(() => {
                  this.zone.run(() => {
                    this.router
                      .navigate(['/auth/authorize'])
                      .catch(navError => {
                        console.error(
                          '[MobileAuthService] Navigation error after forkJoin error:',
                          navError
                        );
                        location.reload();
                      });
                  });
                  return EMPTY;
                })
              );
            })
          );
        }),
        catchError(error => {
          console.error(
            '[MobileAuthService] Error in handleRedirectCallback:',
            error
          );

          const isStateMismatch =
            error?.error === 'state_mismatch' ||
            error?.error_description === 'Invalid state' ||
            error?.message?.includes('state') ||
            error?.message?.includes('mismatch');
          const errorDescription = (
            error?.error_description ?? ''
          ).toLowerCase();
          const errorMessage = (error?.message ?? '').toLowerCase();
          const errorCode = (error?.error ?? '').toLowerCase();
          const isInvalidAuthorizationCode =
            errorCode === 'invalid_grant' ||
            errorDescription.includes('invalid authorization code') ||
            errorMessage.includes('invalid authorization code') ||
            errorMessage.includes('invalid_grant');

          if (isStateMismatch) {
            console.warn(
              '[MobileAuthService] State mismatch detected - clearing Auth0 state and retrying'
            );
            this.clearAuth0CacheState();

            this.resetFlags();

            return from(Browser.close()).pipe(
              catchError(closeError => {
                console.error(
                  '[MobileAuthService] Error closing browser in state mismatch:',
                  closeError
                );
                return of(void 0);
              }),
              switchMap(() => {
                this.zone.run(() => {
                  this.router.navigate(['/auth/authorize']).catch(navError => {
                    console.error(
                      '[MobileAuthService] Navigation error after state mismatch:',
                      navError
                    );
                    location.reload();
                  });
                });
                return EMPTY;
              })
            );
          }

          if (isInvalidAuthorizationCode) {
            console.warn(
              '[MobileAuthService] Invalid authorization code detected - clearing Auth0 state and restarting clean login'
            );
            this.clearAuth0CacheState();
            this.resetFlags();

            return from(Browser.close()).pipe(
              catchError(closeError => {
                console.error(
                  '[MobileAuthService] Error closing browser in invalid authorization code flow:',
                  closeError
                );
                return of(void 0);
              }),
              switchMap(() => this.loginWithRedirect({ cleanState: true })),
              catchError(loginError => {
                console.error(
                  '[MobileAuthService] Error restarting clean login after invalid authorization code:',
                  loginError
                );
                this.zone.run(() => {
                  this.router.navigate(['/auth/authorize']).catch(navError => {
                    console.error(
                      '[MobileAuthService] Navigation error after invalid authorization code:',
                      navError
                    );
                    location.reload();
                  });
                });
                return EMPTY;
              })
            );
          }

          // Otros errores
          this.resetFlags();
          return from(Browser.close()).pipe(
            catchError(closeError => {
              console.error(
                '[MobileAuthService] Error closing browser in catchError:',
                closeError
              );
              return of(void 0);
            }),
            switchMap(() => {
              this.zone.run(() => {
                this.router.navigate(['/auth/authorize']).catch(navError => {
                  console.error(
                    '[MobileAuthService] Navigation error:',
                    navError
                  );
                  location.reload();
                });
              });
              return EMPTY;
            })
          );
        })
      )
      .subscribe({
        next: () => {
          console.log(
            '[MobileAuthService] handleRedirectCallback subscription completed successfully'
          );
        },
        error: error => {
          console.error(
            '[MobileAuthService] Error in handleRedirectCallback subscription:',
            error
          );
          this.isProcessingCallback = false;
        },
        complete: () => {
          console.log(
            '[MobileAuthService] handleRedirectCallback subscription completed'
          );
        },
      });
  }

  // ---------------------------------------------------------------------------
  // LOGOUT CALLBACK (sin state/code)
  // ---------------------------------------------------------------------------
  private handleLogoutCallbackWithoutState(url: string): void {
    console.log('[MobileAuthService] Processing logout callback', { url });

    this.clearLogoutTimeout();

    if (this.isProcessingCallback) {
      console.warn(
        '[MobileAuthService] Already processing logout callback, ignoring duplicate'
      );
      return;
    }

    this.isProcessingCallback = true;

    // Flujo simplificado: cerrar browser -> limpiar estado -> recargar
    from(Browser.close())
      .pipe(
        catchError(closeError => {
          console.warn(
            '[MobileAuthService] Error closing browser (continuing anyway):',
            closeError
          );
          return of(void 0);
        }),
        // NOTE: Trusted-device cleanup. If the user does NOT have biometrics
        // active (either explicitly off or never set), drop the
        // `loggedInDevice` flag so the next login is treated as a first-time
        // login again and the activate-biometrics-first-time prompt is shown.
        // If biometrics IS active we keep the flag intact so the next boot
        // still behaves as a trusted device (Face ID -> silent token).
        switchMap(() =>
          this.preferencesService.getBiometricsPreferences().pipe(
            take(1),
            switchMap(({ value }) => {
              if (value === BiometricSecurityType.on) {
                return of(void 0);
              }
              return this.preferencesService.removeLoggedInDevice();
            }),
            catchError(() => of(void 0))
          )
        ),
        switchMap(() => {
          // NOTE: Keep isLoggingOut = true so the effect logout$ (which reacts
          // to the LogOut action below) calls logOut() but hits the guard and
          // exits immediately. The reload() below destroys the JS context so
          // flags don't need manual cleanup.
          this.store.dispatch(new LogOut());

          return timer(500).pipe(
            take(1),
            tap(() => {
              location.reload();
            })
          );
        }),
        catchError(error => {
          console.error(
            '[MobileAuthService] Error in logout callback flow:',
            error
          );
          this.store.dispatch(new LogOut());

          return timer(500).pipe(
            take(1),
            tap(() => {
              location.reload();
            })
          );
        })
      )
      .subscribe({
        complete: () => {
          console.log(
            '[MobileAuthService] Logout callback processing completed'
          );
        },
      });
  }

  // ---------------------------------------------------------------------------
  // HELPERS
  // ---------------------------------------------------------------------------
  private startLogoutTimeout(): void {
    this.clearLogoutTimeout();
    this.logoutTimeoutSub = timer(10_000)
      .pipe(
        filter(() => !this.isProcessingCallback),
        tap(() => {
          console.warn(
            '[MobileAuthService] Logout callback not received in time, forcing reload'
          );
          Browser.close().catch(() => {});
          this.store.dispatch(new LogOut());
          this.resetFlags();
          this.isLoggingOut = false;
          location.reload();
        })
      )
      .subscribe();
  }

  private clearLogoutTimeout(): void {
    this.logoutTimeoutSub?.unsubscribe();
    this.logoutTimeoutSub = null;
  }

  private resetFlags(): void {
    this.isProcessingCallback = false;
    this.isLoginFlowInProgress = false;
  }

  private clearAuth0CacheState(): void {
    try {
      localStorage.removeItem('auth0-cache');
      localStorage.removeItem('@@auth0spajs@@::' + environment.auth0ClientID);
      sessionStorage.clear();
    } catch (storageError) {
      console.warn(
        '[MobileAuthService] Error clearing auth cache state:',
        storageError
      );
    }
  }

  // Método público para verificar si estamos en proceso de logout
  getIsLoggingOut(): boolean {
    return this.isLoggingOut;
  }

  // NOTE: Lightweight reset used by the authorize recovery UI to retry the
  // login flow without a full page reload. Clears the in-progress guard so a
  // fresh AuthorizeUser dispatch actually reaches loginWithRedirect again,
  // and defensively closes any lingering Browser tab.
  resetLoginFlow(): void {
    this.isLoginFlowInProgress = false;
    this.isProcessingCallback = false;
    Browser.close().catch(() => {});
  }

  // NOTE: Local-only logout used by the inactivity auto-timeout path. Avoids
  // opening the Auth0 logout browser without a user gesture (which races the
  // 10s logout watchdog and can leave the WebView stuck on the recovery
  // screen). We invalidate local Auth0 cache + drop loggedInDevice so the
  // next bootstrap is treated as a fresh login, but keep the biometrics
  // preference intact so Face ID / fingerprint stays usable.
  async localLogoutAndReload(): Promise<void> {
    try {
      this.clearAuth0CacheState();
      const keys = Object.keys(localStorage);
      keys.forEach(key => {
        if (key.startsWith('@@auth0spajs@@')) {
          localStorage.removeItem(key);
        }
      });
    } catch (storageError) {
      console.warn(
        '[MobileAuthService] Error clearing local auth state on inactivity timeout:',
        storageError
      );
    }

    try {
      await Preferences.remove({ key: 'loggedInDevice' });
      await Preferences.remove({ key: 'openId' });
      await Preferences.set({ key: 'LogOut', value: 'true' });
    } catch (prefsError) {
      console.warn(
        '[MobileAuthService] Error clearing native preferences on inactivity timeout:',
        prefsError
      );
    }

    location.reload();
  }

  private redirect(url: string): void {
    const urlParams = new URLSearchParams(url);
    const origin = urlParams.get('origin');
    const queryParams = MobileRoutesHelper.getParams(false, url);

    const urlFor =
      !!origin &&
      origin !== '/' &&
      origin !== '/auth/authorize' &&
      !origin.includes('/core/error')
        ? origin.split('?')[0]
        : '/core/home/';

    this.router.navigate([urlFor], { queryParams });
  }

  /**
   * Wait for biometrics flow to complete before redirecting
   * This prevents race condition between redirect() and securityComplete$ effect
   * that both try to navigate at the same time
   */
  private waitForBiometricsCompletion(url: string): Observable<void> {
    console.log('[MobileAuthService] Waiting for biometrics completion action');

    type CompletionReason = 'completed' | 'first-time-setup' | 'timeout';

    // NOTE: Three-way race:
    //   - completedBiometricsOnRegister → normal path, redirect to origin.
    //   - firstTimeSetupNavigationStarted → start-flow is sending the user to
    //     the activate-biometrics-first-time screen, so we must NOT redirect
    //     (otherwise we race the legitimate navigation and dump the user on
    //     /core/home with the start-flow pipeline forever stuck).
    //   - timer(10000) → safety net, force redirect so we never hang.
    return race<CompletionReason[]>([
      this.actions$.pipe(
        ofType(BiometricsActions.completedBiometricsOnRegister),
        take(1),
        tap(() =>
          console.log(
            '[MobileAuthService] Biometrics completed, proceeding with redirect'
          )
        ),
        map(() => 'completed' as CompletionReason)
      ),
      this.actions$.pipe(
        ofType(BiometricsActions.firstTimeSetupNavigationStarted),
        take(1),
        tap(() =>
          console.log(
            '[MobileAuthService] firstTimeSetupNavigationStarted received, skipping redirect'
          )
        ),
        map(() => 'first-time-setup' as CompletionReason)
      ),
      timer(10000).pipe(
        tap(() =>
          console.log(
            '[MobileAuthService] Biometrics completion timeout, proceeding with redirect anyway'
          )
        ),
        map(() => 'timeout' as CompletionReason)
      ),
    ]).pipe(
      tap(reason => {
        if (reason === 'first-time-setup') {
          // Start-flow is in charge of navigation now; do nothing here.
          return;
        }
        console.log(
          '[MobileAuthService] Redirecting to origin after biometrics completion'
        );
        this.redirect(url);
      }),
      map(() => void 0)
    );
  }

  getAuth0User(): Observable<Auth0User> {
    return this.auth0Service.user$;
  }

  // NOTE: Default cache behaviour. After handleRedirectCallback() the SDK
  // already has a fresh access token in localStorage; forcing `cacheMode: 'off'`
  // here would bypass it and immediately try the refresh-token grant. With
  // `useRefreshTokens: true` that fails with `missing_refresh_token` whenever
  // the Auth0 API does not have "Allow Offline Access" enabled (Auth0 silently
  // drops `offline_access` from the granted scopes in that case, so no refresh
  // token is ever issued even though the SDK is configured to use one).
  // For renewals when the access token expires the SDK will fall back to the
  // refresh token automatically — we only need to skip the cache when we
  // explicitly want a brand-new token (e.g. periodic background refresh).
  getTokenSilently(): Observable<string> {
    return this.auth0Service.getAccessTokenSilently({ cacheMode: 'on' });
  }

  // ---------------------------------------------------------------------------
  // LOGOUT
  // Flujo: Limpiar preferencias -> Abrir browser con Auth0 logout -> Esperar callback
  // ---------------------------------------------------------------------------
  logOut(): Observable<void> {
    if (this.isLoggingOut) {
      console.warn(
        '[MobileAuthService] Logout already in progress, ignoring duplicate call'
      );
      return of(void 0);
    }

    this.isLoggingOut = true;

    // Resetear flags para evitar conflictos con flujos pendientes
    this.resetFlags();

    // NOTE: Safety timeout — if the deep link callback never arrives (network
    // failure, user closes browser manually, Auth0 session expired), force a
    // clean reload after 10 seconds so the app doesn't stay stuck on a spinner.
    this.startLogoutTimeout();

    return from(
      this.auth0Service.logout({
        logoutParams: { returnTo: callbackUri },
        clientId: environment.auth0ClientID,
        openUrl: async (url: string) => {
          if (url?.startsWith('http')) {
            console.log('[MobileAuthService] Opening browser for logout:', url);
            await Browser.open({ url });
          } else {
            console.warn('[MobileAuthService] Invalid logout URL:', url);
          }
        },
      })
    ).pipe(
      catchError(logOutError => {
        console.error('[MobileAuthService] Auth0 logout failed:', logOutError);
        this.clearLogoutTimeout();
        this.resetFlags();
        this.isLoggingOut = false;
        // NOTE: The Auth0 logout browser could not be opened, so the deep-link
        // callback will never arrive. Force a clean reload as a last resort so
        // the app restarts in a consistent state instead of staying stuck.
        setTimeout(() => this.locationService.reload(), 300);
        return of(void 0);
      }),
      // NOTE: Persist the explicit-logout flag so the next bootstrap forces the
      // AuthorizeUser flow even when biometrics is ON (see
      // biometricInitialization$); otherwise the device would silently
      // re-authenticate right after the user asked to log out.
      switchMap(() =>
        this.preferencesService.setLogOut().pipe(
          catchError(preferencesServiceError => {
            console.warn(preferencesServiceError);
            return of(void 0);
          })
        )
      ),
      // NOTE: Do NOT reload here. Reloading right after opening the Auth0
      // logout browser destroys the JS context and resets `isLoggingOut`, so
      // when the deep-link logout callback arrives it re-enters logOut() (the
      // guard no longer blocks) and opens the browser again -> infinite logout
      // loop. The reload is performed by handleLogoutCallbackWithoutState once
      // the callback is received (or by the 10s watchdog), keeping
      // `isLoggingOut` true so the re-dispatched LogOut hits the guard.
      switchMap(() => of(void 0))
    );
  }

  // ---------------------------------------------------------------------------
  // LOGIN
  // ---------------------------------------------------------------------------
  loginWithRedirect(options?: { cleanState?: boolean }): Observable<void> {
    if (this.isLoginFlowInProgress) {
      console.warn(
        '[MobileAuthService] Login flow already in progress, ignoring duplicate call'
      );
      return of(void 0);
    }

    console.log('[MobileAuthService] loginWithRedirect called', {
      cleanState: options?.cleanState,
    });
    this.isLoginFlowInProgress = true;

    // NOTE: When cleanState is true, callers request minimal appState; it is applied only on iOS (see below).
    const loginConfig: {
      openUrl: (url: string) => Promise<void>;
      appState?: Record<string, unknown>;
    } = {
      openUrl: async (url: string) => {
        console.log('[MobileAuthService] openUrl (login) called with:', {
          url,
          type: typeof url,
          length: url?.length,
        });

        // NOTE: Validate URL before opening
        if (!url || typeof url !== 'string' || url.trim().length === 0) {
          const error = new Error('Invalid URL provided');
          console.error('[MobileAuthService] Invalid URL:', error);
          this.isLoginFlowInProgress = false;
          throw error;
        }

        // NOTE: Verify URL is valid using URL constructor
        try {
          new URL(url);
        } catch (urlError) {
          console.error('[MobileAuthService] URL validation failed:', urlError);
          this.isLoginFlowInProgress = false;
          throw new Error('Invalid URL format');
        }

        try {
          await this.openBrowserWithRetry(url);
          console.log('[MobileAuthService] Browser opened successfully');
        } catch (browserError: unknown) {
          const urlObj = new URL(url);
          const isIos = Capacitor.getPlatform() === 'ios';
          const isLikelyTooLong = isIos && url.length >= IOS_URL_LIMIT;

          console.error('[MobileAuthService] Error opening Browser:', {
            error: browserError,
            urlLength: url.length,
            iosUrlLimit: IOS_URL_LIMIT,
            isLikelyTooLong,
            domain: urlObj.hostname,
            pathname: urlObj.pathname,
            queryParamsCount: urlObj.searchParams.size,
            queryParamsLength: urlObj.search.length,
          });

          if (isLikelyTooLong) {
            console.error(
              '[MobileAuthService] Browser.open() failed - URL exceeds iOS SFSafariViewController limit'
            );
          } else {
            console.error(
              '[MobileAuthService] Browser.open() failed after retries - likely timing / UIViewController lifecycle issue'
            );
          }

          this.isLoginFlowInProgress = false;
          this.store.dispatch(
            MobileAuthActions.loginBrowserFailed({
              reason: isLikelyTooLong ? 'invalid-url' : 'browser-open-failed',
              urlLength: url.length,
            })
          );
          throw browserError;
        }
      },
    };

    // NOTE: Empty appState shortens the authorize URL (iOS SFSafariViewController URL limit).
    // Only apply on iOS; Android tolerates longer URLs without this tradeoff.
    if (options?.cleanState && Capacitor.getPlatform() === 'ios') {
      console.log(
        '[MobileAuthService] Using clean state to minimize Auth0 URL length (iOS)'
      );
      loginConfig.appState = {};
    }

    return this.auth0Service.loginWithRedirect(loginConfig).pipe(
      catchError(error => {
        console.error('[MobileAuthService] Error in loginWithRedirect:', error);
        this.isLoginFlowInProgress = false;

        this.zone.run(() => {
          this.router.navigate(['/auth/authorize']).catch(navError => {
            console.error(
              '[MobileAuthService] Navigation error after loginWithRedirect failure:',
              navError
            );
            location.reload();
          });
        });

        return of(void 0);
      })
    );
  }

  // NOTE: Retries Browser.open() up to N attempts with exponential backoff.
  // Mitigates iOS timing issues where UIViewController is not yet ready to
  // present SFSafariViewController (e.g. during app boot or right after
  // biometrics dialog dismissal).
  private async openBrowserWithRetry(url: string): Promise<void> {
    let lastError: unknown;

    for (
      let attempt = 0;
      attempt < BROWSER_OPEN_BACKOFFS_MS.length;
      attempt++
    ) {
      const delayMs = BROWSER_OPEN_BACKOFFS_MS[attempt];
      if (delayMs > 0) {
        await new Promise(resolve => setTimeout(resolve, delayMs));
      }

      try {
        await Browser.open({ url });
        if (attempt > 0) {
          console.log(
            `[MobileAuthService] Browser.open() succeeded on attempt ${attempt + 1}`
          );
        }
        return;
      } catch (error) {
        lastError = error;
        console.warn(
          `[MobileAuthService] Browser.open() attempt ${attempt + 1}/${BROWSER_OPEN_BACKOFFS_MS.length} failed`,
          error
        );
      }
    }

    throw lastError;
  }
}
